import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Image as AntImage, Button, Modal, Upload } from 'antd';
import ImageCropper from 'antd-img-crop';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import ImagePicker from '../../components/form/general/ImagePicker';
import {
  useDeleteSmartPricingRuleImage,
  useGenerateSmartPricingRuleImage,
  useGetSmartPricingRule,
  useUploadSmartPricingRuleImage,
} from '../../views/menu/smart-pricing/hooks/apis';
import { Utils } from '../../services/helpers/Utils';
import { SMART_PRICING_TAGS, smartPricingApiSlice } from '../../store/api-slices/SmartPricingApiSlice';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { AnalyticsEvent, DiscardAction, GenerationStatus } from '../../services/analytics/events';
import RegenerationReasonModal from './RegenerationReasonModal';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 90_000;
const PROGRESS_LABELS = ['cooking', 'plating', 'finishing'];
const PROGRESS_ROTATE_MS = 9_000;
const CROPPER_PROPS = {
  aspect: 16 / 9,
  rotationSlider: false,
  aspectSlider: false,
  cropShape: 'rect',
};

function formatRemaining(remainingMs, t) {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));

  if (totalSeconds < 60) {
    return t('time.seconds', { count: totalSeconds });
  }

  const minutes = Math.ceil(totalSeconds / 60);

  if (minutes < 60) {
    return t('time.minutes', { count: minutes });
  }

  const hours = Math.ceil(minutes / 60);

  return t('time.hours', { count: hours });
}

function ruleIdentityProps(rule) {
  return {
    smart_pricing_rule_id: rule?.id,
    smart_pricing_rule_name: rule?.name ?? null,
    restaurant_id: rule?.company_id ?? null,
  };
}

export default function SmartPricingRuleImagesModal({ rule, open, onClose, onChange }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Marketing:SmartPricingRuleImagesModal' });
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const [uploadSmartPricingRuleImage] = useUploadSmartPricingRuleImage();
  const [deleteSmartPricingRuleImage] = useDeleteSmartPricingRuleImage();
  const [generateSmartPricingRuleImage] = useGenerateSmartPricingRuleImage();

  const [currentImageUrl, setCurrentImageUrl] = useState(rule?.image_url ?? null);
  const [currentImageMeta, setCurrentImageMeta] = useState(rule?.image_meta ?? null);
  const [step, setStep] = useState(() => (rule?.image_url ? 'ready' : 'select'));
  const [progressIndex, setProgressIndex] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [rateLimit, setRateLimit] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const startUrlRef = useRef(rule?.image_url ?? null);
  const pollTimeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const cropperContainerRef = useRef(null);
  const attemptCountRef = useRef(0);
  const inFlightRef = useRef(null);

  const isGenerating = step === 'generating';
  const { data: polledRule } = useGetSmartPricingRule(rule?.id, {
    skip: !isGenerating,
    pollingInterval: POLL_INTERVAL_MS,
  });
  const polledImageUrl = polledRule?.image_url ?? null;
  const polledImageMeta = polledRule?.image_meta ?? null;

  const applyImageChange = useCallback(
    (nextImageUrl, nextImageMeta = null) => {
      setCurrentImageUrl(nextImageUrl);
      setCurrentImageMeta(nextImageMeta);

      if (onChange) {
        onChange(nextImageUrl);
      }
    },
    [onChange],
  );

  const rateLimitExpired = rateLimit && now >= rateLimit.expiresAt;
  const isRateLimited = rateLimit && !rateLimitExpired;

  useEffect(() => {
    if (!isRateLimited) {
      return undefined;
    }

    const id = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(id);
  }, [isRateLimited]);

  useEffect(() => {
    if (rateLimitExpired) {
      setRateLimit(null);
    }
  }, [rateLimitExpired]);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (!open) {
      stopPolling();
      setStep(currentImageUrl ? 'ready' : 'select');
      setProgressIndex(0);
      setPreviewImage(null);
      startUrlRef.current = currentImageUrl;
      attemptCountRef.current = 0;
      inFlightRef.current = null;
      setReasonModalOpen(false);
    }
  }, [open, currentImageUrl, stopPolling]);

  const uploadFile = useCallback(
    async (file) => {
      const priorWasAi = currentImageMeta?.is_ai_generated === true;
      const priorAttempts = attemptCountRef.current;

      const res = await uploadSmartPricingRuleImage(rule.id, Utils.createFormData({ image: file }));

      if (!res.error) {
        const nextUrl = res.data?.image_url ?? null;
        const nextMeta = res.data?.image_meta ?? null;

        if (priorWasAi) {
          track(AnalyticsEvent.SmartPricingRuleImageDiscarded, {
            ...ruleIdentityProps(rule),
            total_generations: priorAttempts,
            discard_action: DiscardAction.ReplacedWithUpload,
          });
        }

        startUrlRef.current = nextUrl;
        applyImageChange(nextUrl, nextMeta);
        setStep('ready');
        setPreviewImage(null);
        toast.success(t('toasts.upload_success'));

        return;
      }

      toast.error(t('toasts.upload_failed'));
    },
    [applyImageChange, currentImageMeta, rule, t, track, uploadSmartPricingRuleImage],
  );

  const triggerCropForFile = useCallback((file) => {
    const input = cropperContainerRef.current?.querySelector('input[type="file"]');

    if (!input) {
      return false;
    }

    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    return true;
  }, []);

  const openFilePicker = useCallback(() => {
    const button = cropperContainerRef.current?.querySelector('button[data-role="upload-trigger"]');
    button?.click();
  }, []);

  const startGenerate = useCallback(
    async ({ reason = null, reasonText = null } = {}) => {
      attemptCountRef.current += 1;
      const generationIndex = attemptCountRef.current;
      const isRegeneration = generationIndex > 1;
      const baseEventProps = {
        ...ruleIdentityProps(rule),
        generation_index: generationIndex,
        is_regeneration: isRegeneration,
        regeneration_reason: isRegeneration ? reason : null,
        regeneration_reason_text: isRegeneration ? reasonText : null,
      };
      inFlightRef.current = { ...baseEventProps, requestedAt: Date.now() };

      startUrlRef.current = rule?.image_url ?? null;
      setPreviewImage(null);
      setStep('generating');
      setProgressIndex(0);

      const res = await generateSmartPricingRuleImage(rule.id);

      if (res?.error?.scope === 'company' || res?.error?.scope === 'smart_pricing_rule') {
        const retryAfter = Math.max(1, Number(res.error.retry_after) || 60);
        track(AnalyticsEvent.SmartPricingRuleImageGenerated, {
          ...baseEventProps,
          generation_status: GenerationStatus.RateLimited,
          generation_latency_ms: Date.now() - inFlightRef.current.requestedAt,
          rate_limit_scope: res.error.scope,
          retry_after_seconds: retryAfter,
        });
        inFlightRef.current = null;
        setRateLimit({
          scope: res.error.scope,
          retryAfter,
          expiresAt: Date.now() + retryAfter * 1000,
        });
        setStep('select');
        toast.error(
          t(`toasts.rate_limit_${res.error.scope}`, {
            time: formatRemaining(retryAfter * 1000, t),
          }),
        );

        return;
      }

      if (res?.error) {
        track(AnalyticsEvent.SmartPricingRuleImageGenerated, {
          ...baseEventProps,
          generation_status: GenerationStatus.Failed,
          generation_latency_ms: Date.now() - inFlightRef.current.requestedAt,
          error_reason: res.error?.message ?? res.error?.code ?? 'unknown',
        });
        inFlightRef.current = null;
        setStep('select');
        toast.error(t('toasts.generate_failed'));

        return;
      }

      progressIntervalRef.current = setInterval(() => {
        setProgressIndex((prev) => Math.min(prev + 1, PROGRESS_LABELS.length - 1));
      }, PROGRESS_ROTATE_MS);

      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        const inFlight = inFlightRef.current;
        if (inFlight) {
          track(AnalyticsEvent.SmartPricingRuleImageGenerated, {
            smart_pricing_rule_id: inFlight.smart_pricing_rule_id,
            smart_pricing_rule_name: inFlight.smart_pricing_rule_name,
            restaurant_id: inFlight.restaurant_id,
            generation_index: inFlight.generation_index,
            is_regeneration: inFlight.is_regeneration,
            regeneration_reason: inFlight.regeneration_reason,
            regeneration_reason_text: inFlight.regeneration_reason_text,
            generation_status: GenerationStatus.Timeout,
            generation_latency_ms: POLL_TIMEOUT_MS,
          });
          inFlightRef.current = null;
        }

        setStep('select');
        toast.error(t('toasts.generate_timeout'));
      }, POLL_TIMEOUT_MS);
    },
    [generateSmartPricingRuleImage, rule, stopPolling, t, track],
  );

  useEffect(() => {
    if (!isGenerating || !polledImageUrl || polledImageUrl === startUrlRef.current) {
      return;
    }

    stopPolling();

    const inFlight = inFlightRef.current;
    if (inFlight) {
      track(AnalyticsEvent.SmartPricingRuleImageGenerated, {
        smart_pricing_rule_id: inFlight.smart_pricing_rule_id,
        smart_pricing_rule_name: inFlight.smart_pricing_rule_name,
        restaurant_id: inFlight.restaurant_id,
        generation_index: inFlight.generation_index,
        is_regeneration: inFlight.is_regeneration,
        regeneration_reason: inFlight.regeneration_reason,
        regeneration_reason_text: inFlight.regeneration_reason_text,
        generation_status: GenerationStatus.Success,
        generation_latency_ms: Date.now() - inFlight.requestedAt,
        prompt_template_version: polledImageMeta?.prompt_template_version ?? null,
      });
      inFlightRef.current = null;
    }

    startUrlRef.current = polledImageUrl;
    applyImageChange(polledImageUrl, polledImageMeta);
    dispatch(smartPricingApiSlice.util.invalidateTags([SMART_PRICING_TAGS.SMART_PRICING_RULES]));
    toast.success(t('toasts.generate_success'));

    fetch(polledImageUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const extension = blob.type === 'image/jpeg' ? 'jpg' : 'png';
        const file = new File([blob], `smart-pricing-rule-${rule.id}-ai.${extension}`, {
          type: blob.type || 'image/png',
        });
        setPreviewImage({ url: polledImageUrl, file });
        setStep('preview');
      })
      .catch(() => {
        setPreviewImage({ url: polledImageUrl, file: null });
        setStep('preview');
      });
  }, [applyImageChange, dispatch, isGenerating, rule, polledImageUrl, polledImageMeta, stopPolling, t, track]);

  const onInitialGenerate = useCallback(() => {
    startGenerate();
  }, [startGenerate]);

  const onRegenerateClick = useCallback(() => {
    setReasonModalOpen(true);
  }, []);

  const onReasonConfirm = useCallback(
    (reason, reasonText) => {
      setReasonModalOpen(false);
      startGenerate({ reason, reasonText });
    },
    [startGenerate],
  );

  const onReasonCancel = useCallback(() => {
    setReasonModalOpen(false);
  }, []);

  const continueToEdit = useCallback(() => {
    if (!previewImage?.file) {
      if (previewImage?.url) {
        applyImageChange(previewImage.url);
      }

      setStep('ready');
      setPreviewImage(null);

      return;
    }

    const triggered = triggerCropForFile(previewImage.file);

    if (!triggered) {
      if (previewImage?.url) {
        applyImageChange(previewImage.url);
      }

      setStep('ready');
      setPreviewImage(null);
    }
  }, [applyImageChange, previewImage, triggerCropForFile]);

  const onDelete = useCallback(async () => {
    const priorWasAi = currentImageMeta?.is_ai_generated === true;
    const priorAttempts = attemptCountRef.current;

    const res = await deleteSmartPricingRuleImage(rule.id);

    if (!res.error) {
      if (priorWasAi) {
        track(AnalyticsEvent.SmartPricingRuleImageDiscarded, {
          ...ruleIdentityProps(rule),
          total_generations: priorAttempts,
          discard_action: DiscardAction.Removed,
        });
      }

      startUrlRef.current = null;
      applyImageChange(null);
      setStep('select');
      toast.success(t('toasts.delete_success'));

      return;
    }

    toast.error(t('toasts.delete_failed'));
  }, [applyImageChange, currentImageMeta, deleteSmartPricingRuleImage, rule, t, track]);

  const imagePickerImages = useMemo(() => (currentImageUrl ? [currentImageUrl] : []), [currentImageUrl]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t('title')}
      width={step === 'preview' ? 520 : 480}
      maskClosable={step !== 'generating'}
    >
      <HiddenCropperUpload containerRef={cropperContainerRef} onPickedFile={uploadFile} />

      {step === 'select' && (
        <SelectState
          t={t}
          onGenerate={onInitialGenerate}
          onUploadClick={openFilePicker}
          rateLimit={isRateLimited ? rateLimit : null}
          now={now}
        />
      )}

      {step === 'generating' && <GeneratingState t={t} progressKey={PROGRESS_LABELS[progressIndex]} />}

      {step === 'preview' && previewImage && (
        <PreviewState t={t} imageUrl={previewImage.url} onRegenerate={onRegenerateClick} onContinue={continueToEdit} />
      )}

      {step === 'ready' && (
        <div className="mt-2xs">
          <ImagePicker
            images={imagePickerImages}
            onChange={() => {}}
            onAdd={uploadFile}
            onDelete={onDelete}
            maxCount={1}
            format="image/png,image/jpeg"
            withCropper
            cropperProps={CROPPER_PROPS}
          />
        </div>
      )}

      <RegenerationReasonModal open={reasonModalOpen} onCancel={onReasonCancel} onConfirm={onReasonConfirm} />
    </Modal>
  );
}

function HiddenCropperUpload({ containerRef, onPickedFile }) {
  const handleBeforeUpload = (file) => {
    onPickedFile(file);

    return false;
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    >
      <ImageCropper {...CROPPER_PROPS}>
        <Upload showUploadList={false} accept="image/png,image/jpeg" beforeUpload={handleBeforeUpload}>
          <button data-role="upload-trigger" type="button">
            upload
          </button>
        </Upload>
      </ImageCropper>
    </div>
  );
}

function SelectState({ t, onGenerate, onUploadClick, rateLimit, now }) {
  const cardClasses = [
    'flex flex-col items-center gap-md',
    'rounded-2xl border border-dashed border-brand-inkGrey-grey_3_line',
    'bg-white px-lg py-xl',
  ].join(' ');
  const iconWrapperClasses = [
    'flex h-12 w-12 items-center justify-center',
    'rounded-2xl bg-brand-inkGreen-green_0 text-brand-inkGreen-green_2',
  ].join(' ');

  return (
    <div className="py-xs">
      {rateLimit && (
        <Alert
          className="mb-md"
          type="warning"
          showIcon
          title={t(`select.rate_limit_${rateLimit.scope}.title`)}
          description={t(`select.rate_limit_${rateLimit.scope}.description`, {
            time: formatRemaining(rateLimit.expiresAt - now, t),
          })}
        />
      )}
      <div className={cardClasses}>
        <div className={iconWrapperClasses}>
          <Sparkles size={26} strokeWidth={1.75} />
        </div>
        <div className="text-center">
          <div className="text-sm font-semibold text-brand-text-default">{t('select.generate_title')}</div>
          <div className="mt-2xs text-xs text-brand-text-grey">{t('select.generate_subtitle')}</div>
        </div>
        <Button
          type="primary"
          onClick={onGenerate}
          icon={<Sparkles size={14} />}
          size="middle"
          disabled={Boolean(rateLimit)}
        >
          {t('select.generate_cta')}
        </Button>
      </div>

      <div className="mt-lg flex justify-center">
        <Button type="text" onClick={onUploadClick} icon={<UploadOutlined />}>
          {t('select.upload_link')}
        </Button>
      </div>
    </div>
  );
}

function GeneratingState({ t, progressKey }) {
  return (
    <div className="flex flex-col items-center gap-md py-xl">
      <LoadingOutlined style={{ fontSize: 40, color: '#69CE8C' }} spin />
      <div className="text-center">
        <div className="text-sm font-semibold text-brand-text-default">{t('generating.title')}</div>
        <div className="mt-2xs text-xs text-brand-text-grey">{t(`generating.progress.${progressKey}`)}</div>
      </div>
    </div>
  );
}

function PreviewState({ t, imageUrl, onRegenerate, onContinue }) {
  const frameClasses = [
    'rounded-2xl overflow-hidden',
    'border border-brand-inkGrey-grey_2',
    'bg-brand-inkGrey-grey_1',
  ].join(' ');

  return (
    <div className="py-xs">
      <div className={frameClasses}>
        <AntImage
          src={imageUrl}
          alt={t('preview.alt')}
          wrapperClassName="block w-full"
          rootClassName="block w-full"
          width="100%"
          style={{
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            cursor: 'zoom-in',
            display: 'block',
          }}
          preview={{
            mask: <div className="text-xs font-medium">{t('preview.click_to_expand')}</div>,
          }}
        />
      </div>

      <div className="mt-lg flex items-center justify-between">
        <div className="text-xs text-brand-text-grey">{t('preview.hint')}</div>
        <div className="flex gap-xs">
          <Button onClick={onRegenerate} icon={<Sparkles size={14} />}>
            {t('preview.regenerate')}
          </Button>
          <Button type="primary" onClick={onContinue}>
            {t('preview.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
}
